import { useTranscription } from '@src/stores-v2/StoreContext';
import { observer } from 'mobx-react-lite';
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { TranscriptionText } from '../../transcript/TranscriptionText';
import { findQuoteMatchRange } from './findQuoteMatchRange';

interface QuoteCardTranscriptPanelProps {
  quote: string;
}

/**
 * Read-only transcript panel for the pull quote modal.
 * Renders the full story transcript with timecodes and speaker labels.
 * Words in the region matching the quote keep the normal color; all other
 * words are dimmed to grey.
 */
export const QuoteCardTranscriptPanel: React.FC<QuoteCardTranscriptPanelProps> =
  observer(({ quote }) => {
    const transcriptionStore = useTranscription();
    const scrollRef = useRef<HTMLDivElement>(null);
    // Use originalTranscription.elements (the full story transcript) instead of
    // getFinalTranscriptionElements() which gets overwritten by EditorStore.refreshTranscript()
    // with the current video's clip-specific elements.
    const originalTranscription = transcriptionStore.getOriginalTranscription();
    const transcriptElements = originalTranscription?.elements;

    const matchRange = useMemo(() => {
      if (!transcriptElements?.length || !quote) return null;
      return findQuoteMatchRange(transcriptElements, quote);
    }, [transcriptElements, quote]);

    // Apply grey to non-matching elements and auto-scroll to the match
    useLayoutEffect(() => {
      if (!scrollRef.current || !matchRange) return;
      const container = scrollRef.current;
      const wordEls = container.querySelectorAll<HTMLElement>('[data-index]');
      let firstMatchEl: HTMLElement | null = null;

      wordEls.forEach(el => {
        const index = Number(el.getAttribute('data-index'));
        if (index >= matchRange.start && index <= matchRange.end) {
          el.style.color = '';
          if (!firstMatchEl) firstMatchEl = el;
        } else {
          el.style.color = '#666';
        }
      });

      // Scroll the matching region into view
      if (firstMatchEl) {
        (firstMatchEl as HTMLElement).scrollIntoView({
          block: 'center',
          inline: 'start',
        });
      }
    }, [matchRange, transcriptElements]);

    if (!transcriptElements?.length) {
      return (
        <LoadingContainer>
          <LoadingText>Loading transcript...</LoadingText>
        </LoadingContainer>
      );
    }

    return (
      <TranscriptContainer ref={scrollRef}>
        <TranscriptionText
          cursor={{ from: -1, to: -1 }}
          transcriptElements={transcriptElements}
          scrollRef={scrollRef}
          isPlaying={false}
          punchListSignature=""
          getPhotoHighlightViewData={() => ({
            punchListItem: undefined,
            lastPhotoHighlightActive: false,
            lastPhotoHighlightElementId: undefined,
          })}
          hidePhotoHighlights
        />
      </TranscriptContainer>
    );
  });

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const TranscriptContainer = styled.div`
  width: 100%;
  height: calc(100vh - 350px);
  min-height: 300px;
  overflow-y: auto;
  padding-right: 8px;
  padding-bottom: 24px;
  box-sizing: border-box;
  line-height: 175%;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  width: 100%;
`;

const LoadingText = styled.span`
  color: #7a7a9a;
  font-size: 14px;
`;
